import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Text,
  Title,
  Stepper,
  useMantineTheme,
  TextInput,
  List,
  CopyButton,
  Tooltip,
  ActionIcon,
  Loader,
  Flex,
  Modal,
} from "@mantine/core";
import "@valikwp/react-file-manager/dist/style.css";
import { useStylesProviders } from "../../utils/mantine/useStylesProviders";
import { useStylesUserTabContent } from "../../utils/mantine/useStylesUserTabContent";
import CustomButton from "../mantineCustomComponents/CustomButton";
import { useStylesButton } from "../../utils/mantine/useStylesButton";
import { useForm } from "@mantine/form";
import {
  getInteractiveBrokersProviders,
  URLs,
  fetchInteractiveBrokersAccount,
  deleteProviderRequest,
} from "../../utils/apis";
import { formatHexStr } from "../../utils/formatHexStr";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import CustomAnchor from "../mantineCustomComponents/CustomAnchor";
import { useStylesIcons } from "../../utils/mantine/useStylesIcons";

const Providers = () => {
  const theme = useMantineTheme();

  //css classes
  const { classes: classesProviders } = useStylesProviders();
  const { classes: classesUserTabContent } = useStylesUserTabContent();
  const { classes: classesStylesButton } = useStylesButton();
  const { classes: classesIcons } = useStylesIcons();

  // Modal delete provider
  const [openedModal, setOpenedModal] = useState(false);
  const [deleteLabel, setDeleteLabel] = useState(null);

  const openModal = (label) => {
    setDeleteLabel(label);
    setOpenedModal(true);
  };

  const closeModal = () => {
    setOpenedModal(false);
    setDeleteLabel(null);
  };

  const [active, setActive] = useState(0);

  // Flag to indicate whether the Interactive Brokers onboarding flow has started
  const [interactiveBrokersStarted, setInteractiveBrokersStarted] =
    useState(false);

  /**
   * Trigger the start of the Interactive Brokers onboarding flow.
   */
  const interactiveBrokersStart = () => {
    setInteractiveBrokersStarted(true);
  };

  const form = useForm({
    mode: "uncontrolled",
    collectionName: "",
    collectionDescription: "",
    // functions will be used to validate values at corresponding key
    validate: {
      providerNameInteractiveBroker: (value) => {
        let foundUnsafeChars;
        if (value) {
          const unsafeCharacters = /[<>#%{}|\\^~[\]`]/g;
          foundUnsafeChars = value.match(unsafeCharacters);
        }
        if ((value && (value.length < 3 || value.length > 250)) || !value) {
          return "Value error: Name must be between 3 and 250 characters long.";
        } else if (foundUnsafeChars) {
          return `Invalid name. Character not allowed: ${[
            ...new Set(foundUnsafeChars),
          ].join(", ")}`;
        }

        return null;
      },
    },
  });

  const [loader, setLoader] = useState(true);

  const [loaderAccountInfo, setLoaderAccountInfo] = useState(true);
  const [loaderButton, setLoaderButton] = useState(false);

  const [loaderButtons, setLoaderButtons] = useState([]);

  const [interactiveBrokersProvider, setInteractiveBrokersProvider] = useState(
    [],
  );

  // mapping steps with wizard_step
  const getStepFromWizard = (wizardStep) => {
    switch (wizardStep) {
      case "step_1_onboarding_started":
        return 1;
      case "step_2_credentials_connected":
        return 2;
      case "step_3_onboarding_completed":
        return 3;
      case "disabled":
      case "not_configured":
      default:
        return 0;
    }
  };

  // Success after IB
  const refs = useRef({});
  const [searchParams] = useSearchParams();
  const success = searchParams.get("success") === "True";
  const label = searchParams.get("label");

  useEffect(() => {
    if (success && label) {
      setLoaderAccountInfo(true);
      const fetchAll = async () => {
        await fetchInteractiveBrokersAccount(
          label,
          setAccountInfo,
          setLoaderAccountInfo,
          setLoaderButtons,
        );

        const el = refs.current[label];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));

        const data = await getInteractiveBrokersProviders();
        setInteractiveBrokersProvider(data);
      };

      fetchAll();
    }
  }, [success, label]);

  // delete provider
  const handleDeleteProvider = async () => {
    setLoaderButton(true);
    await deleteProviderRequest(deleteLabel, {
      onSuccess: reloadProviders,
      onFinally: () => {
        closeModal();
        setLoaderButton(false);
      },
    });
  };

  // reconnect provider
  const handleReconnectProvider = (name) => {
    setLoaderButtons((prev) => ({ ...prev, [name]: true }));
    const fetchInteractiveBrokersAccountSetCollection = async () => {
      await fetchInteractiveBrokersAccount(
        name,
        setAccountInfo,
        setLoaderAccountInfo,
        setLoaderButtons,
        true,
      );
    };

    fetchInteractiveBrokersAccountSetCollection();
  };

  const [reloadKeyProviders, setReloadKeyProviders] = useState(0);
  const reloadProviders = useCallback(() => {
    setReloadKeyProviders((prev) => prev + 1);
  }, []);

  const [accountInfo, setAccountInfo] = useState({});
  useEffect(() => {
    const fetchInteractiveBrokersProviders = async () => {
      const data = await getInteractiveBrokersProviders();

      setInteractiveBrokersProvider(data);
      setLoader(false);
    };

    fetchInteractiveBrokersProviders();
  }, [accountInfo, reloadKeyProviders]);

  return (
    <Box
      className={`${classesProviders.providers} ${classesUserTabContent.tabBox}`}
      pos="relative"
    >
      <Title order={2}>Connect Your Broker</Title>
      <Text className="text" pb={theme.padding.lgEm}>
        Link your brokerage account to validityBase to automatically verify and
        showcase your live trading performance
      </Text>
      <Box className="content">
        {!loader ? (
          <>
            <Title order={4}>Interactive Brokers</Title>
            {interactiveBrokersProvider.length !== 0 && (
              <List type="ordered" size="sm" className={classesProviders.list}>
                {interactiveBrokersProvider.map((item, index) => (
                  <List.Item
                    key={index}
                    ref={(el) => {
                      if (el) {
                        refs.current[item.label] = el;
                      }
                    }}
                  >
                    <Box key={index}>
                      {item.label && (
                        <Text>
                          <Text component="span">Label:</Text> {item.label}
                        </Text>
                      )}
                      {item.collection_name && (
                        <Text>
                          <Text component="span">Collection Name:</Text>{" "}
                          {item.collection_name}
                        </Text>
                      )}
                      {item.collection_cid && (
                        <Text title={item.collection_cid}>
                          <Text component="span">Collection Hash:</Text>{" "}
                          {formatHexStr(item.collection_cid)}
                          <CopyButton
                            value={item.collection_cid}
                            timeout={2000}
                          >
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied" : "Copy"}
                                className={classesIcons.copy}
                                withArrow
                                position="right"
                              >
                                <ActionIcon
                                  color={
                                    copied
                                      ? theme.customColors.actionIconFirst
                                      : theme.customColors.actionIconSecond
                                  }
                                  variant="subtle"
                                  onClick={copy}
                                >
                                  {copied ? (
                                    <IconCheck className={classesIcons.width} />
                                  ) : (
                                    <IconCopy className={classesIcons.width} />
                                  )}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Text>
                      )}
                      {item.last_stamping_date && (
                        <Text>
                          <Text component="span">Last Stamping Date:</Text>{" "}
                          {item.last_stamping_date}
                        </Text>
                      )}

                      {item.last_stamping_attempt_date && (
                        <Text>
                          <Text component="span">
                            Last Stamping Attempt Date:
                          </Text>{" "}
                          {item.last_stamping_attempt_date}
                        </Text>
                      )}

                      {item.last_stamping_attempt_status && (
                        <Text>
                          <Text component="span">
                            Last Stamping Attempt Status:
                          </Text>{" "}
                          {item.last_stamping_attempt_status}
                        </Text>
                      )}

                      {item.last_stamping_attempt_error && (
                        <Text className="error">
                          <Text component="span">
                            Last Stamping Attempt Error:
                          </Text>{" "}
                          {item.last_stamping_attempt_error}
                        </Text>
                      )}

                      {item.provider_name && (
                        <Text>
                          <Text component="span">Broker:</Text>{" "}
                          {item.provider_name}
                        </Text>
                      )}

                      {accountInfo.accountId && (
                        <Text>
                          <Text component="span">Account ID:</Text>{" "}
                          {accountInfo.accountId}
                        </Text>
                      )}
                      {accountInfo.accountTitle && (
                        <Text>
                          <Text component="span">Account Owner:</Text>{" "}
                          {accountInfo.accountTitle}
                        </Text>
                      )}

                      {label === item.label && (
                        <>
                          <Stepper
                            pt={theme.padding.baseEm}
                            color={theme.customColors.black}
                            active={getStepFromWizard(item.wizard_step)}
                            allowNextStepsSelect={false}
                          >
                            <Stepper.Step
                              label="First step"
                              description="Create Label"
                            >
                              Please select a label for this account. This label
                              is just for you to recognize the account:
                              <form
                                onSubmit={form.onSubmit(async (values) => {
                                  setLoaderAccountInfo(true);

                                  await fetchInteractiveBrokersAccount(
                                    values.providerNameInteractiveBroker,
                                    setAccountInfo,
                                    setLoaderAccountInfo,
                                    setLoaderButton,
                                  );
                                })}
                              >
                                <TextInput
                                  classNames={{
                                    input:
                                      "provider-name-input provider-interactive-broker-name-input",
                                  }}
                                  label=""
                                  inputWrapperOrder={["label", "input"]}
                                  inputContainer={(children) => (
                                    <Box>
                                      {children}
                                      <Box className="provider-name-error-text">
                                        {
                                          form.errors
                                            .providerNameInteractiveBroker
                                        }
                                      </Box>
                                      <Box className="provider-name-created-text"></Box>
                                    </Box>
                                  )}
                                  placeholder="Enter Label"
                                  key={form.key(
                                    "providerNameInteractiveBroker",
                                  )}
                                  {...form.getInputProps(
                                    "providerNameInteractiveBroker",
                                  )}
                                />

                                <CustomButton
                                  text="Next"
                                  componentClasses={`${classesStylesButton.buttonBlack} provider-submit`}
                                ></CustomButton>
                              </form>
                            </Stepper.Step>
                            <Stepper.Step
                              label="Second step"
                              description="Interactive Brokers Log In"
                            >
                              <CustomAnchor
                                text="Go to Interactive Brokers"
                                href={`${URLs.INTERACTIVE_BROKERS_AUTHORIZATION_URL}?label=${item.collection_name}`}
                                componentClasses="link anchor link_padding_0 collection-name"
                                color={theme.customColors.black}
                              ></CustomAnchor>
                            </Stepper.Step>
                            <Stepper.Step
                              label="Final step"
                              description="Collection Creation. Wait 20 seconds."
                            ></Stepper.Step>
                            <Stepper.Completed>
                              {!loaderAccountInfo && <Text>Completed</Text>}
                            </Stepper.Completed>
                          </Stepper>

                          {loaderAccountInfo && (
                            <Flex
                              className="loader"
                              align={theme.flex.align.center}
                              justify={theme.flex.justify.center}
                            >
                              <Loader color="black" />
                            </Flex>
                          )}
                        </>
                      )}

                      <CustomButton
                        text="Delete Provider"
                        variant="transparent"
                        onClick={() => openModal(item.label)}
                        componentClasses={`${classesStylesButton.link} ${classesStylesButton.linkRed} deleter-provider`}
                      ></CustomButton>

                      {item.last_stamping_attempt_error && (
                        <CustomButton
                          text="Reconnect Provider"
                          variant="transparent"
                          disabled={loaderButtons[item.label]}
                          loading={loaderButtons[item.label]}
                          componentClasses={`${classesStylesButton.link}`}
                          color={theme.customColors.black}
                          onClick={() => handleReconnectProvider(item.label)}
                        ></CustomButton>
                      )}
                    </Box>
                  </List.Item>
                ))}
              </List>
            )}
            {!interactiveBrokersStarted && (
              <>
                {interactiveBrokersProvider.length === 0 && (
                  <Text pb={theme.padding.baseEm}>
                    Secure, read-only connection. We can&apos;t trade or
                    transfer money — we can only view your portfolio.
                  </Text>
                )}
                <CustomButton
                  text={
                    interactiveBrokersProvider.length === 0
                      ? "Link Account"
                      : "Add another account"
                  }
                  onClick={() => interactiveBrokersStart()}
                  componentClasses={`${classesStylesButton.buttonBlack} provider-button`}
                ></CustomButton>
              </>
            )}
            {interactiveBrokersStarted && (
              <Stepper
                color={theme.customColors.black}
                active={active}
                onStepClick={setActive}
                allowNextStepsSelect={false}
              >
                <Stepper.Step label="First step" description="Create Label">
                  Please select a label for this account. This label is just for
                  you to recognize the account:
                  <form
                    onSubmit={form.onSubmit(async (values) => {
                      await fetchInteractiveBrokersAccount(
                        values.providerNameInteractiveBroker,
                        setAccountInfo,
                        setLoaderAccountInfo,
                        setLoaderButton,
                      );
                    })}
                  >
                    <TextInput
                      classNames={{
                        input:
                          "provider-name-input provider-interactive-broker-name-input",
                      }}
                      label=""
                      inputWrapperOrder={["label", "input"]}
                      inputContainer={(children) => (
                        <Box>
                          {children}
                          <Box className="provider-name-error-text">
                            {form.errors.providerNameInteractiveBroker}
                          </Box>
                          <Box className="provider-name-created-text"></Box>
                        </Box>
                      )}
                      placeholder="Enter Label"
                      key={form.key("providerNameInteractiveBroker")}
                      {...form.getInputProps("providerNameInteractiveBroker")}
                    />

                    <CustomButton
                      text="Next"
                      componentClasses={`${classesStylesButton.buttonBlack} provider-submit`}
                    ></CustomButton>
                  </form>
                </Stepper.Step>
                <Stepper.Step
                  label="Second step"
                  description="Interactive Brokers Log In"
                ></Stepper.Step>
                <Stepper.Step
                  label="Final step"
                  description="Collection Creation. Wait 20 seconds."
                >
                  Confirm Collection Creation
                </Stepper.Step>
                <Stepper.Completed>
                  Completed, click back button to get to previous step
                </Stepper.Completed>
              </Stepper>
            )}
          </>
        ) : (
          <Flex
            className="loader"
            align={theme.flex.align.center}
            justify={theme.flex.justify.center}
          >
            <Loader color="black" />
          </Flex>
        )}
      </Box>

      <Modal opened={openedModal} onClose={closeModal} title="Delete Provider">
        <Text
          classNames={{
            root: classesProviders.ProvidersModalText,
          }}
        >
          Confirm Deleting Provider?
        </Text>

        <Flex
          justify={theme.flex.justify.center}
          gap={theme.flex.gap.large}
          mb={theme.margin.xl}
        >
          <CustomButton
            text="Yes"
            loading={loaderButton}
            componentClasses="buttonBlack modal-button-delete-provider"
            onClick={() => handleDeleteProvider()}
          ></CustomButton>
          <CustomButton
            text="No"
            componentClasses="buttonWhite"
            onClick={() => closeModal()}
          ></CustomButton>
        </Flex>
      </Modal>
    </Box>
  );
};

Providers.propTypes = {};

export default Providers;
